/**
 * folderTraversal.ts
 *
 * LEÍRÁS:
 * Ez a modul rekurzív mappa bejárást valósít meg a File System Access API és
 * a webkitGetAsEntry API segítségével. Megoldja a következő problémákat:
 *
 * 1. Drag&Drop esetén a fájlok rossz helyre kerültek (felső szintre)
 * 2. Almappákból hiányoztak fájlok
 * 3. Mappanév nem lett figyelembe véve
 *
 * HASZNÁLAT:
 * ```
 * import { traverseDataTransferItems } from './folderTraversal';
 *
 * const files = await traverseDataTransferItems(event.dataTransfer.items);
 * // files: FileWithPath[] - fájlok a teljes útvonallal
 * ```
 *
 * LÉTREHOZVA: 2026-01-05
 * KÖVETELMÉNY: 2.1 Mappa kiválasztás (Kívánságlista)
 */
const CHUNK_SIZE = 100;
/**
 * Fő belépési pont: DataTransferItemList bejárása
 * Kezeli mind a fájlokat, mind a mappákat rekurzívan
 * Chunked feldolgozással a böngésző lefagyásának elkerülésére
 *
 * @param items - A drag&drop eseményből származó DataTransferItemList
 * @param onProgress - Opcionális progress callback
 * @returns Promise<FileWithPath[]> - Az összes fájl a teljes útvonallal
 */
export async function traverseDataTransferItems(items, onProgress) {
    const allFiles = [];
    const ctx = { processedCount: 0 };
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry?.();
            if (entry) {
                await traverseEntryChunked(entry, '', allFiles, onProgress, ctx);
            }
            else {
                const file = item.getAsFile();
                if (file) {
                    allFiles.push({
                        file,
                        relativePath: file.name,
                    });
                }
            }
        }
    }
    if (allFiles.length > 0 && ctx.processedCount % CHUNK_SIZE !== 0) {
        onProgress?.(allFiles.length, '');
    }
    return allFiles;
}
/**
 * Rekurzív bejárás chunked feldolgozással
 * Minden CHUNK_SIZE fájl után yieldelünk az event loopnak
 *
 * @param entry - FileSystemEntry (fájl vagy mappa)
 * @param parentPath - A szülő mappa útvonala
 * @param allFiles - A gyűjtő tömb az összes fájlhoz
 * @param onProgress - Opcionális progress callback
 * @param ctx - Kontextus a processedCount nyilvántartásához
 */
async function traverseEntryChunked(entry, parentPath, allFiles, onProgress, ctx) {
    const context = ctx || { processedCount: 0 };
    const currentPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
    if (entry.isFile) {
        const fileEntry = entry;
        try {
            const file = await getFileFromEntry(fileEntry);
            allFiles.push({
                file,
                relativePath: currentPath,
            });
            context.processedCount++;
            if (context.processedCount % CHUNK_SIZE === 0) {
                onProgress?.(context.processedCount, currentPath);
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }
        catch (error) {
            console.error(`Hiba a fájl olvasásakor: ${currentPath}`, error);
        }
    }
    else if (entry.isDirectory) {
        const dirEntry = entry;
        await traverseDirectoryChunked(dirEntry, currentPath, allFiles, onProgress, context);
    }
}
/**
 * Rekurzív bejárás egy FileSystemEntry-n (legacy)
 * Ha mappa, akkor bejárja az összes gyerekét
 * Ha fájl, akkor hozzáadja a listához
 *
 * @param entry - FileSystemEntry (fájl vagy mappa)
 * @param parentPath - A szülő mappa útvonala
 * @param allFiles - A gyűjtő tömb az összes fájlhoz
 */
async function traverseEntry(entry, parentPath, allFiles) {
    const currentPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
    if (entry.isFile) {
        const fileEntry = entry;
        try {
            const file = await getFileFromEntry(fileEntry);
            allFiles.push({
                file,
                relativePath: currentPath,
            });
        }
        catch (error) {
            console.error(`Hiba a fájl olvasásakor: ${currentPath}`, error);
        }
    }
    else if (entry.isDirectory) {
        const dirEntry = entry;
        await traverseDirectory(dirEntry, currentPath, allFiles);
    }
}
/**
 * Mappa tartalmának bejárása chunked feldolgozással
 *
 * @param dirEntry - A mappa bejegyzés
 * @param dirPath - A mappa teljes útvonala
 * @param allFiles - A gyűjtő tömb
 * @param onProgress - Opcionális progress callback
 * @param ctx - Kontextus a processedCount nyilvántartásához
 */
async function traverseDirectoryChunked(dirEntry, dirPath, allFiles, onProgress, ctx) {
    const reader = dirEntry.createReader();
    let entries = [];
    const readBatch = () => {
        return new Promise((resolve, reject) => {
            reader.readEntries(resolve, reject);
        });
    };
    let batch = await readBatch();
    while (batch.length > 0) {
        entries = entries.concat(batch);
        batch = await readBatch();
    }
    for (const entry of entries) {
        await traverseEntryChunked(entry, dirPath, allFiles, onProgress, ctx);
    }
}
/**
 * Mappa tartalmának bejárása (legacy)
 * A DirectoryReader.readEntries() csak korlátozott számú bejegyzést ad vissza,
 * ezért ciklusban kell hívni amíg üres tömböt nem kapunk
 *
 * @param dirEntry - A mappa bejegyzés
 * @param dirPath - A mappa teljes útvonala
 * @param allFiles - A gyűjtő tömb
 */
async function traverseDirectory(dirEntry, dirPath, allFiles) {
    const reader = dirEntry.createReader();
    let entries = [];
    const readBatch = () => {
        return new Promise((resolve, reject) => {
            reader.readEntries(resolve, reject);
        });
    };
    let batch = await readBatch();
    while (batch.length > 0) {
        entries = entries.concat(batch);
        batch = await readBatch();
    }
    const promises = entries.map((entry) => traverseEntry(entry, dirPath, allFiles));
    await Promise.all(promises);
}
/**
 * File objektum kinyerése FileSystemFileEntry-ből
 *
 * @param fileEntry - A fájl bejegyzés
 * @returns Promise<File> - A File objektum
 */
function getFileFromEntry(fileEntry) {
    return new Promise((resolve, reject) => {
        fileEntry.file(resolve, reject);
    });
}
/**
 * FileWithPath tömb átalakítása File[] tömbbé
 * A file.path tulajdonságot beállítja a relatív útvonalra
 *
 * @param filesWithPath - A FileWithPath tömb
 * @returns File[] - File objektumok path tulajdonsággal
 */
export function convertToFilesWithPath(filesWithPath) {
    return filesWithPath.map(({ file, relativePath }) => {
        try {
            Object.defineProperty(file, 'path', {
                value: relativePath,
                writable: false,
                configurable: true,
            });
            Object.defineProperty(file, 'webkitRelativePath', {
                value: relativePath,
                writable: false,
                configurable: true,
            });
        }
        catch (error) {
            console.warn(`Nem sikerült beállítani a path-ot: ${relativePath}`);
        }
        return file;
    });
}
export function buildFolderStructure(files) {
    const root = {
        name: 'root',
        path: '',
        type: 'folder',
        children: [],
    };
    const folderCache = new Map();
    folderCache.set('', root);
    for (const file of files) {
        const path = file.path || file.name;
        const parts = path.split('/').filter(Boolean);
        let parentPath = '';
        let current = root;
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            const isLast = i === parts.length - 1;
            const currentPath = parentPath ? `${parentPath}/${part}` : part;
            if (isLast) {
                current.children.push({
                    name: part,
                    path: currentPath,
                    type: 'file',
                    children: [],
                    file,
                    size: file.size,
                });
            }
            else {
                let folder = folderCache.get(currentPath);
                if (!folder) {
                    folder = {
                        name: part,
                        path: currentPath,
                        type: 'folder',
                        children: [],
                    };
                    current.children.push(folder);
                    folderCache.set(currentPath, folder);
                }
                current = folder;
                parentPath = currentPath;
            }
        }
    }
    return root;
}
export async function buildFolderStructureAsync(files, onProgress) {
    const root = {
        name: 'root',
        path: '',
        type: 'folder',
        children: [],
    };
    const folderCache = new Map();
    folderCache.set('', root);
    const BATCH_SIZE = 100;
    const total = files.length;
    for (let batchStart = 0; batchStart < total; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, total);
        for (let j = batchStart; j < batchEnd; j++) {
            const file = files[j];
            const path = file.path || file.name;
            const parts = path.split('/').filter(Boolean);
            let parentPath = '';
            let current = root;
            for (let i = 0; i < parts.length; i++) {
                const part = parts[i];
                const isLast = i === parts.length - 1;
                const currentPath = parentPath ? `${parentPath}/${part}` : part;
                if (isLast) {
                    current.children.push({
                        name: part,
                        path: currentPath,
                        type: 'file',
                        children: [],
                        file,
                        size: file.size,
                    });
                }
                else {
                    let folder = folderCache.get(currentPath);
                    if (!folder) {
                        folder = {
                            name: part,
                            path: currentPath,
                            type: 'folder',
                            children: [],
                        };
                        current.children.push(folder);
                        folderCache.set(currentPath, folder);
                    }
                    current = folder;
                    parentPath = currentPath;
                }
            }
        }
        onProgress?.(batchEnd, total);
        if (batchEnd < total) {
            await new Promise(resolve => setTimeout(resolve, 0));
        }
    }
    return root;
}
/**
 * Összesített statisztikák kiszámítása a fájlokból
 *
 * @param files - File[] tömb
 * @returns Statisztikák objektum
 */
export function calculateUploadStats(files) {
    const folders = new Set();
    for (const file of files) {
        const path = file.path || file.name;
        const parts = path.split('/').filter(Boolean);
        for (let i = 0; i < parts.length - 1; i++) {
            folders.add(parts.slice(0, i + 1).join('/'));
        }
    }
    return {
        totalFiles: files.length,
        totalSize: files.reduce((sum, f) => sum + f.size, 0),
        folderCount: folders.size,
        folders,
    };
}
